import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Radio,
  Typography,
  message,
  Tag,
  Popconfirm,
  Space,
  Image,
  Spin,
  Tooltip,
} from "antd";
import { decompressData, EXCHANGE_INSTRUMENT, generateNewRelicLink, ALGO_TYPE, INSTRUMENT } from "../../utils";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "../../graphql/queries";
import TradeDetailsTable from "./TradeDetailsTable";
import { tradeStatusToColor } from "../accountOverview/GroupTable";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Service from "../../services";
import { ENGINE, isEngineChoiceShown } from "../../utils/engine";
import EngineChoice from "../EngineChoice";
import styles from "../../pages/main/AccountDetails/index.module.scss";
import shareImg from "../../assets/images/share.svg";
import SpotTradeS3Service from "../../services/SpotTradeS3Service";

dayjs.extend(relativeTime);
const { Option } = Select;
const { Title, Text } = Typography;

const ALGO_SUPPORTED_EXCHANGES = {
  [INSTRUMENT.spot_algo]: [
    {
      label: "Binance spot algo",
      value: EXCHANGE_INSTRUMENT.binance_spot_algo,
    },
    {
      label: "Okx spot algo",
      value: EXCHANGE_INSTRUMENT.okx_spot_algo,
    },
  ],
  [INSTRUMENT.future_algo]: [
    {
      label: "Binance future algo",
      value: EXCHANGE_INSTRUMENT.binance_future_algo,
    },
  ],
};

const AlgoTab = ({ futureTradeAccountStatus, algoInstrumentType }) => {
  const [form] = Form.useForm();
  const quantityType = Form.useWatch("quantityType", form);
  const [submittedForm] = Form.useForm();
  const [algoTradeAccountStatus, setAlgoTradeAccountStatus] = useState();
  const [algoTrades, setAlgoTrades] = useState(null);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState(null);
  const [pauseEngine, setPauseEngine] = useState(ENGINE.legacy);

  // Cache management - track the last processed URL to avoid unnecessary refetches
  const [lastProcessedUrl, setLastProcessedUrl] = useState(null);

  const algoExchange = getAlgoInstrumentFromFutureInstrument(futureTradeAccountStatus.exchange, algoInstrumentType);
  const account = futureTradeAccountStatus.account;
  const sessionName = `${algoExchange}_${account}`;
  const supportedExchanges = ALGO_SUPPORTED_EXCHANGES[algoInstrumentType] || [];

  // Helper function to parse CSV into trade objects
  const parseCsvToTrades = (csvText) => {
    const lines = csvText.trim().split("\n");
    if (lines.length < 2) return []; // Need at least header + 1 data row

    // Parse header
    const headers = lines[0].split(",");

    // Parse data rows
    const trades = lines
      .slice(1)
      .map((line) => {
        if (!line.trim()) return null; // Skip empty lines

        const values = line.split(",");
        const trade = {};

        headers.forEach((header, index) => {
          const value = values[index];

          // Convert numeric fields
          if (["timestamp", "price", "amount", "cost", "fee_cost"].includes(header)) {
            trade[header] = parseFloat(value) || 0;
          } else {
            trade[header] = value || "";
          }
        });

        return trade;
      })
      .filter((trade) => trade !== null);

    return trades;
  };

  // Refresh presigned URL and return the new URL (for immediate use)
  const refreshPresignedUrlAndGetNew = useCallback(
    async (expiredUrl, accountSymbol) => {
      try {
        console.log("Refreshing expired presigned URL...");

        // Generate new presigned URL using spot trade S3 service
        const newPresignedUrl = await SpotTradeS3Service.regeneratePresignedUrl(expiredUrl);

        // Update DAO with new URL (don't wait for this)
        Service.updateAlgoTradesUrl({
          exchange: algoExchange,
          accountSymbol: accountSymbol,
          algoTrades: newPresignedUrl,
        }).catch((error) => {
          console.error("Failed to update DAO with new presigned URL:", error);
        });

        // Update local cache state to prevent unnecessary refetch on next poll
        setLastProcessedUrl(newPresignedUrl);

        console.log("Successfully refreshed presigned URL and updated local cache");
        return newPresignedUrl;
      } catch (error) {
        console.error("Failed to refresh presigned URL:", error);
        message.error(`Failed to refresh trades access: ${error.message}`);
        return null;
      }
    },
    [algoExchange]
  );

  // Fetch trades data from S3 URL or legacy compressed format
  const fetchTradesData = useCallback(
    async (algoTradesData, accountSymbol, retryCount = 0) => {
      try {
        if (!algoTradesData) return [];

        // Check if it's an S3 presigned URL (starts with https://)
        if (algoTradesData.startsWith("https://")) {
          // Check if URL is expired or about to expire (only on first attempt)
          if (retryCount === 0 && SpotTradeS3Service.isUrlExpiredOrExpiring(algoTradesData)) {
            console.log("Presigned URL is expired or expiring soon, regenerating and retrying...");
            const newUrl = await refreshPresignedUrlAndGetNew(algoTradesData, accountSymbol);
            if (newUrl) {
              return await fetchTradesData(newUrl, accountSymbol, retryCount + 1);
            }
            return null;
          }

          // Fetch CSV from S3
          const response = await fetch(algoTradesData);

          if (!response.ok) {
            // If 400 Bad Request or 403 Forbidden and haven't retried yet, URL might be expired
            if ((response.status === 400 || response.status === 403) && retryCount === 0) {
              console.log(`Got ${response.status} error, URL likely expired, regenerating and retrying...`);
              const newUrl = await refreshPresignedUrlAndGetNew(algoTradesData, accountSymbol);
              if (newUrl) {
                return await fetchTradesData(newUrl, accountSymbol, retryCount + 1);
              }
              return null;
            }
            throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
          }

          const csvText = await response.text();

          // Parse CSV into trade objects and convert to legacy format for TradeDetailsTable
          const trades = parseCsvToTrades(csvText);

          // Convert to standardized format expected by TradeDetailsTable
          const tradeDataKeys = ["timestamp", "price", "amount", "cost", "takerOrMaker", "fee"];
          const tradeDataValues = trades.map((trade) =>
            tradeDataKeys.map((key) => {
              if (key === "fee") {
                return { cost: trade.fee_cost || 0, currency: trade.fee_currency || "" };
              }
              return trade[key];
            })
          );

          return {
            keys: tradeDataKeys,
            values: tradeDataValues,
          };
        } else {
          // Fallback: Handle old compressed format for backward compatibility
          const decompressedData = decompressData(algoTradesData);
          return JSON.parse(decompressedData);
        }
      } catch (error) {
        console.error("Error fetching trades:", error);
        throw error;
      }
    },
    [refreshPresignedUrlAndGetNew]
  );

  const fetchAlgoTradeAccountStatus = useCallback(async () => {
    try {
      const {
        data: { listTradeAccountStatuses: tradeAccountStatuses },
      } = await API.graphql(
        graphqlOperation(queries.listTradeAccountStatuses, {
          exchange: algoExchange,
          accountSymbol: {
            beginsWith: account,
          },
        })
      );
      if (tradeAccountStatuses && tradeAccountStatuses.items.length > 0) {
        tradeAccountStatuses.items.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        const latestTradeAccountStatus = tradeAccountStatuses.items[0];
        setAlgoTradeAccountStatus(latestTradeAccountStatus);
        submittedForm.resetFields();
        if (latestTradeAccountStatus.algoParams) {
          const algoParams = JSON.parse(latestTradeAccountStatus.algoParams);
          submittedForm.setFieldsValue(algoParams);
        }
      } else {
        setAlgoTradeAccountStatus(null);
      }
    } catch (error) {
      message.error(String(error));
    }
  }, [algoExchange, account, submittedForm]);

  const onFinish = (formParams) => {
    const startTradeParams = {
      sessionName,
      exchange: algoExchange,
      account,
      pair: formParams.symbol,
      algoParams: JSON.stringify(formParams),
    };
    Service.startTradeExecution(startTradeParams)
      .then((response) => {
        if (!response || typeof response !== "string") {
          console.log({ response });
          throw new Error(`Something is wrong: response is malformed`);
        }
        message.success(`
            Action will begin in 15 seconds. ${response}
            `);
      })
      .catch((error) => {
        console.log("Validate Failed:", error);
        console.log(error);
        message.error(error.message ? error.message : String(error));
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log(errorInfo);
    message.error(errorInfo.errorFields[0].errors);
  };

  // Effect to handle trades data fetching when algoTradeAccountStatus changes
  useEffect(() => {
    const fetchTradesIfNeeded = async () => {
      if (!algoTradeAccountStatus) {
        setAlgoTrades(null);
        setLastProcessedUrl(null);
        return;
      }

      // Fetch trades data (S3 or legacy compressed format) - only if URL has changed
      const currentTradesUrl = algoTradeAccountStatus.algoTrades;

      // Since presigned URLs change on each S3 upload, we only need to check URL changes
      // This automatically detects when new trade data is uploaded to S3
      const shouldRefetchTrades = currentTradesUrl && currentTradesUrl !== lastProcessedUrl;

      console.log("Trade URL comparison:", {
        account: `${algoExchange}_${account}`,
        currentUrl: currentTradesUrl,
        lastProcessedUrl: lastProcessedUrl,
        shouldRefetch: shouldRefetchTrades,
      });

      if (shouldRefetchTrades) {
        setTradesLoading(true);
        setTradesError(null);
        try {
          const tradesData = await fetchTradesData(currentTradesUrl, algoTradeAccountStatus.accountSymbol);

          // If tradesData is null, it means URL was refreshed and we should wait for next update
          if (tradesData === null) {
            console.log("URL was refreshed, waiting for next account status update...");
            setTradesLoading(false);
            return;
          }

          setAlgoTrades(tradesData);

          // Update cache tracking
          setLastProcessedUrl(currentTradesUrl);
        } catch (tradesError) {
          console.error("Failed to fetch trades data:", tradesError);
          setTradesError(tradesError.message);
          setAlgoTrades(null);
        } finally {
          setTradesLoading(false);
        }
      } else if (currentTradesUrl) {
        // DAO has same presigned URL - use cached data (no new trades uploaded)
      } else {
        // No trades data available
        setAlgoTrades(null);
        setLastProcessedUrl(null);
      }
    };

    fetchTradesIfNeeded();
  }, [lastProcessedUrl, algoExchange, account, fetchTradesData, algoTradeAccountStatus]);

  useEffect(() => {
    // Reset cache when account changes to ensure fresh data fetch
    setLastProcessedUrl(null);
    setAlgoTrades(null);
    setTradesError(null);

    fetchAlgoTradeAccountStatus();
    const timer = setInterval(() => {
      fetchAlgoTradeAccountStatus();
    }, 30 * 1000);
    return () => clearInterval(timer);
  }, [futureTradeAccountStatus.account, futureTradeAccountStatus.exchange, fetchAlgoTradeAccountStatus]);

  if (!supportedExchanges.map((e) => e.value).find((e) => e.includes(algoExchange))) {
    return (
      <Alert
        message={`${algoInstrumentType === INSTRUMENT.spot_algo ? "Spot" : "Future"} algo is not supported for this exchange`}
        type="info"
      />
    );
  }

  // const inputColSpan = { xs: 16, sm: 6, md: 6, lg: 6, xl: 6, xxl: 6 };
  const inputColSpan = 6;
  // const descriptionColSpan = { xs: 24, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 };
  const descriptionColSpan = 16;
  const gutter = [8, 24];
  const formItemLayout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 20 },
  };
  const formButtonLayout = {
    wrapperCol: { offset: 4 },
  };

  const algoParams = algoTradeAccountStatus ? JSON.parse(algoTradeAccountStatus.algoParams) : {};

  const FORM_TYPE = {
    newForm: "newForm",
    submittedForm: "submittedForm",
  };

  const copyPreviousSettings = () => {
    const previousValues = submittedForm.getFieldsValue();
    form.setFieldsValue(previousValues);
    message.success("Previous settings copied successfully");
  };

  const getFormByType = (formType) => {
    const formProperties = {
      form: formType === FORM_TYPE.newForm ? form : submittedForm,
      initialValues: formType === FORM_TYPE.submittedForm ? { ...algoParams, ...algoTradeAccountStatus } : {},
      disabled: formType === FORM_TYPE.submittedForm,
    };
    // last trade form is disabled, the text color is grey by default, make it more visible
    const formTextStyle = formType === FORM_TYPE.submittedForm ? { color: "black" } : {};
    return (
      <Form {...formItemLayout} {...formProperties} style={{ color: "black" }} onFinish={onFinish} onFinishFailed={onFinishFailed}>
        <Form.Item label="Algo Type" name="algoType" required initialValue={ALGO_TYPE.TWAP}>
          <Radio.Group>
            <Radio value={ALGO_TYPE.TWAP}>
              <Text style={formTextStyle}>{ALGO_TYPE.TWAP}</Text>
            </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Exchange" required>
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Form.Item
                name="exchangeInstrument"
                noStyle
                rules={[{ required: true }]}
                initialValue={supportedExchanges.filter(({ value }) => value === algoExchange)[0]?.value}
              >
                <Select placeholder="Please select exchange" onChange={() => {}}>
                  {supportedExchanges
                    .filter(({ value }) => value === algoExchange)
                    .map((e, index) => (
                      <Option key={index} value={e.value}>
                        <Text style={formTextStyle}>{e.label}</Text>
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>Exchange Instrument</span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="Symbol" required>
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Form.Item
                name="symbol"
                noStyle
                rules={[
                  { required: true },
                  {
                    validator: (_, value) => (value && value.includes("/") ? Promise.resolve() : Promise.reject("Should contain '/'")),
                  },
                ]}
              >
                <Input placeholder="BTC/USDT" style={formTextStyle} />
              </Form.Item>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>Trading symbol e.g. BTC/USDT</span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="Side" required>
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Form.Item
                name="side"
                noStyle
                rules={[
                  { required: true },
                  {
                    validator: (_, value) => {
                      const currentQuantityType = formProperties.form.getFieldValue("quantityType");
                      const currentQuantityValue = formProperties.form.getFieldValue("quantityValue");

                      if (currentQuantityType === "positionSide") {
                        if (currentQuantityValue === "LONG" && value !== "sell") {
                          return Promise.reject("When position side is LONG, side must be SELL");
                        }
                        if (currentQuantityValue === "SHORT" && value !== "buy") {
                          return Promise.reject("When position side is SHORT, side must be BUY");
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  placeholder="Please select side"
                  onChange={() => {
                    // Trigger validation on quantityValue when side changes
                    formProperties.form.validateFields(["quantityValue"]).catch(() => {
                      // Validation errors are displayed in the form, no need to handle here
                    });
                  }}
                >
                  {["buy", "sell"].map((value, index) => (
                    <Option key={value} value={value}>
                      <Text style={formTextStyle}>{value}</Text>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>Trading side (BUY or SELL)</span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="DurationInMin" required>
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Form.Item name="durationInMin" noStyle rules={[{ required: true }]}>
                <InputNumber placeholder="Duration in minutes" style={{ ...formTextStyle, width: "100%" }} min={0.01} step={10} />
              </Form.Item>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>Duration in minutes</span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="Quantity" required>
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item name="quantityType" noStyle rules={[{ required: true, message: "Please select quantity type" }]}>
                  <Select
                    placeholder="Type"
                    style={{ width: "50%" }}
                    onChange={() => {
                      // Trigger validation on both side and quantityValue when quantityType changes
                      formProperties.form.validateFields(["side", "quantityValue"]).catch(() => {
                        // Validation errors are displayed in the form, no need to handle here
                      });
                    }}
                  >
                    <Option value="baseQuantity">
                      <Text style={formTextStyle}>Base Quantity</Text>
                    </Option>
                    <Option value="quoteQuantity">
                      <Text style={formTextStyle}>Quote Quantity</Text>
                    </Option>
                    {algoInstrumentType === INSTRUMENT.future_algo && (
                      <Option value="positionSide">
                        <Text style={formTextStyle}>Position Side</Text>
                      </Option>
                    )}
                  </Select>
                </Form.Item>
                {quantityType === "positionSide" ? (
                  <Form.Item
                    name="quantityValue"
                    noStyle
                    rules={[
                      { required: true, message: "Please select position side" },
                      {
                        validator: (_, value) => {
                          const currentSide = formProperties.form.getFieldValue("side");

                          if (value === "LONG" && currentSide && currentSide !== "sell") {
                            return Promise.reject("When position side is LONG, side must be SELL");
                          }
                          if (value === "SHORT" && currentSide && currentSide !== "buy") {
                            return Promise.reject("When position side is SHORT, side must be BUY");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    initialValue="LONG"
                  >
                    <Select
                      placeholder="Side"
                      style={{ width: "50%" }}
                      onChange={() => {
                        // Trigger validation on side when quantityValue changes
                        formProperties.form.validateFields(["side"]).catch(() => {
                          // Validation errors are displayed in the form, no need to handle here
                        });
                      }}
                    >
                      <Option value="LONG">
                        <Text style={formTextStyle}>LONG</Text>
                      </Option>
                      <Option value="SHORT">
                        <Text style={formTextStyle}>SHORT</Text>
                      </Option>
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item name="quantityValue" noStyle rules={[{ required: true, message: "Please input quantity value" }]}>
                    <InputNumber placeholder="Value" style={{ ...formTextStyle, width: "50%" }} min={0.01} step={1} />
                  </Form.Item>
                )}
              </Space.Compact>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>
                Base Quantity: Amount in base currency (e.g., 1 BTC). <br />
                Quote Quantity: Amount in quote currency (e.g., 100 USDT). <br />
                {algoInstrumentType === INSTRUMENT.future_algo && (
                  <>Position Side: Uses all existing contracts for the selected position side (LONG/SHORT) as the total quantity.</>
                )}
              </span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="TriggerPrice">
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Form.Item name="triggerPrice" noStyle>
                <InputNumber
                  placeholder="Optional: Trigger Price"
                  style={{ ...formTextStyle, width: "100%" }}
                  min={0.01}
                  step={10}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>Optional: Once market price hits trigger price, order will execute immediately regardless of other conditions</span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="LimitPrice">
          <Row gutter={gutter}>
            <Col span={inputColSpan}>
              <Form.Item name="limitPrice" noStyle>
                <InputNumber
                  placeholder="Optional: Limit Price"
                  style={{ ...formTextStyle, width: "100%" }}
                  min={0.01}
                  step={10}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={descriptionColSpan} hidden={formType === FORM_TYPE.submittedForm}>
              <span>Optional: Maximum price when buying, minimum price when selling</span>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item {...formButtonLayout} hidden={formType === FORM_TYPE.submittedForm}>
          <Space>
            <Tooltip title="Copy values from previous submission">
              <Button type="default" onClick={copyPreviousSettings}>
                Copy Previous Settings
              </Button>
            </Tooltip>
            <Popconfirm
              title={
                <div style={{ maxWidth: "300px" }}>
                  <div>Start trading on spot?</div>
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                    Note: If there's an arbitrage session trading, spot trading will wait until the arbitrage session becomes "monitoring"
                    or "positionClosed".
                  </div>
                </div>
              }
              onConfirm={() => formProperties.form.submit()}
              placement="rightTop"
              okText="Confirm"
              cancelText="Cancel"
            >
              <Button type="primary" htmlType="button">
                Submit
              </Button>
            </Popconfirm>
          </Space>
        </Form.Item>
      </Form>
    );
  };

  return (
    <>
      {algoTradeAccountStatus ? (
        <>
          <div>
            <Row>
              <Col span={14}>{getFormByType(FORM_TYPE.newForm)}</Col>
              <Col span={10}>
                <Title level={4}>
                  Last Trade Status:
                  <Space>
                    <Button
                      className={styles.shareBtn}
                      type="link"
                      href={generateNewRelicLink(algoExchange, account, algoTradeAccountStatus.symbol)}
                      target="_blank"
                    >
                      <Image width={17} height={17} preview={false} src={shareImg}></Image>
                      {algoInstrumentType === INSTRUMENT.spot_algo ? "Spot" : "Future"} Trade Logs
                    </Button>
                  </Space>
                </Title>
                <Space>
                  Status:
                  <Tag color={tradeStatusToColor[algoTradeAccountStatus.status]}>
                    {algoTradeAccountStatus.status + (algoTradeAccountStatus.errorMessage ? "(error)" : "")}
                  </Tag>
                  {algoTradeAccountStatus.errorMessage && <div>{algoTradeAccountStatus.errorMessage}</div>}
                  Updated at: {dayjs(algoTradeAccountStatus.updatedAt).fromNow()}
                  <Popconfirm
                    placement="rightTop"
                    title={`pause current execution？`}
                    description={isEngineChoiceShown() ? <EngineChoice engine={pauseEngine} onChange={setPauseEngine} /> : undefined}
                    onOpenChange={(open) => open && setPauseEngine(ENGINE.legacy)}
                    onConfirm={async () => {
                      try {
                        if (!sessionName) {
                          throw new Error("Can't find sessionName for this trade session");
                        }
                        const stopTradeExecutionResult = await Service.stopTradeExecution(
                          {
                            exchange: algoExchange,
                            account,
                            sessionName,
                          },
                          pauseEngine
                        );
                        message.success(stopTradeExecutionResult);
                      } catch (error) {
                        // console.error(error);
                        message.error(error.message ? error.message : String(error));
                      }
                    }}
                    okText="Confirm"
                    cancelText="Cancel"
                  >
                    <Button disabled={false}>Pause</Button>
                  </Popconfirm>
                </Space>
                {getFormByType(FORM_TYPE.submittedForm)}
              </Col>
            </Row>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0 }}>
                Trade Details:
              </Title>
              {algoTradeAccountStatus?.algoTrades?.startsWith("https://") && (
                <Button type="primary" size="small" href={algoTradeAccountStatus.algoTrades} target="_blank" download>
                  Download Trades History
                </Button>
              )}
            </div>
            <div>
              {tradesLoading ? (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 8 }}>Loading trades data...</div>
                </div>
              ) : tradesError ? (
                <Alert message="Failed to load trades data" description={tradesError} type="error" showIcon />
              ) : (
                <TradeDetailsTable standardizedTradeData={algoTrades} symbol={algoParams?.symbol} side={algoParams?.side} />
              )}
            </div>
          </div>
        </>
      ) : (
        <div>
          Please create {algoInstrumentType === INSTRUMENT.spot_algo ? "spot" : "future"} algo session in Admin page before trading.
        </div>
      )}
    </>
  );
};

function getAlgoInstrumentFromFutureInstrument(futureInstrument, algoInstrumentType) {
  // For spot algo
  if (algoInstrumentType === INSTRUMENT.spot_algo) {
    switch (futureInstrument) {
      case EXCHANGE_INSTRUMENT.binance_usds_future:
      case EXCHANGE_INSTRUMENT.binance_coin_future:
      case EXCHANGE_INSTRUMENT.binance_spot:
        return EXCHANGE_INSTRUMENT.binance_spot_algo;
      case EXCHANGE_INSTRUMENT.okx_usds_future:
        return EXCHANGE_INSTRUMENT.okx_spot_algo;
      default:
        console.error(`getAlgoInstrumentFromFutureInstrument(): unsupported futureInstrument ${futureInstrument} for spot_algo`);
        return futureInstrument;
    }
  }

  // For future algo
  if (algoInstrumentType === INSTRUMENT.future_algo) {
    switch (futureInstrument) {
      case EXCHANGE_INSTRUMENT.binance_usds_future:
      case EXCHANGE_INSTRUMENT.binance_coin_future:
      case EXCHANGE_INSTRUMENT.binance_spot:
        return EXCHANGE_INSTRUMENT.binance_future_algo;
      default:
        console.error(`getAlgoInstrumentFromFutureInstrument(): unsupported futureInstrument ${futureInstrument} for future_algo`);
        return futureInstrument;
    }
  }

  console.error(`getAlgoInstrumentFromFutureInstrument(): unsupported algoInstrumentType ${algoInstrumentType}`);
  return futureInstrument;
}

export default AlgoTab;
